import Icon from "@mui/material/Icon";

/**
 * Maps API URL paths to actual dashboard routes
 * @param {string} apiUrl - URL from API response (e.g., "dashboard/view/Dashboard", "apps/task")
 * @returns {string} - Dashboard route path
 */
const mapUrlToRoute = (apiUrl) => {
  const urlMapping = {
    "dashboard/view/Dashboard": "", // Empty string for default dashboard route
    "apps/task": "tasks",
    "apps/tasks": "tasks",
    "apps/missions": "missions",
    "apps/entities": "entities",
    "apps/payments": "payments",
    "apps/users": "users",
    "apps/leaderboard": "leaderboards",
    "apps/leaderboards": "leaderboards",
    "apps/pricing": "pricing",
  };

  // Extract the key part from URL (e.g., "apps/task" -> "task")
  const urlKey = apiUrl.split("/").pop().toLowerCase();

  // Try direct mapping first
  if (urlMapping[apiUrl] !== undefined) {
    return urlMapping[apiUrl];
  }

  // Try to find by last segment
  for (const [key, value] of Object.entries(urlMapping)) {
    if (key.endsWith(`/${urlKey}`)) {
      return value;
    }
  }

  // Fallback: try to extract route from URL
  if (apiUrl.includes("dashboard")) {
    return ""; // Default dashboard route
  }
  if (apiUrl.includes("apps/")) {
    const route = apiUrl.split("apps/")[1];
    return route || "";
  }

  // Default fallback
  return "";
};

/**
 * Converts icon name string to Material UI Icon component
 * @param {string} iconName - Icon name from API (e.g., "chart-dots-3", "checklist")
 * @returns {ReactElement} - Material UI Icon component
 */
const getIconComponent = (iconName) => {
  if (!iconName) {
    return <Icon fontSize="small">folder</Icon>;
  }

  // Map common icon names to Material UI icons
  const iconMapping = {
    "chart-dots-3": "insights",
    checklist: "checklist",
    "business entity": "business",
    "file-dollar": "payments",
    "user-scan": "people",
    award: "emoji_events",
    dashboard: "dashboard",
    analytics: "insights",
    users: "people",
    entities: "business",
    payments: "payments",
    leaderboards: "leaderboard",
    missions: "flight",
    tasks: "task_alt",
    task: "task_alt",
    pricing: "sell",
  };

  // Convert icon name to lowercase and try to match
  const normalizedIconName = iconName.toLowerCase().trim();

  // Try direct mapping
  if (iconMapping[normalizedIconName]) {
    return <Icon fontSize="small">{iconMapping[normalizedIconName]}</Icon>;
  }

  // Try to find partial match
  for (const [key, value] of Object.entries(iconMapping)) {
    if (normalizedIconName.includes(key) || key.includes(normalizedIconName)) {
      return <Icon fontSize="small">{value}</Icon>;
    }
  }

  // Fallback: use icon name directly (Material UI might support it)
  return <Icon fontSize="small">{normalizedIconName.replace(/-/g, "_")}</Icon>;
};

/**
 * Transforms profile menu items from API response to Sidenav routes format
 * @param {Array} features - Array of feature objects from profile
 * @returns {Array} - Array of route objects for Sidenav
 */
export const transformProfileMenuToRoutes = (features) => {
  if (!features || !Array.isArray(features) || features.length === 0) {
    return [];
  }

  // Collect all menu items from all features
  const allMenuItems = [];

  features.forEach((feature) => {
    if (feature.menu && Array.isArray(feature.menu)) {
      feature.menu.forEach((menuItem) => {
        // Only include visible menu items
        if (menuItem.isVisible !== false) {
          allMenuItems.push({
            ...menuItem,
            featureName: feature.name,
          });
        }
      });
    }
  });

  // Sort by order
  allMenuItems.sort((a, b) => (a.order || 0) - (b.order || 0));

  // Transform to Sidenav route format
  return allMenuItems.map((menuItem) => {
    const routePath = mapUrlToRoute(menuItem.url);
    const menuName =
      routePath === "requests"
        ? "Flight Requests"
        : menuItem.heading || menuItem.description || "Menu Item";
    // Use routePath for key, fallback to heading-based key
    const routeKey =
      routePath ||
      menuItem.heading?.toLowerCase().replace(/\s+/g, "-") ||
      "menu-item";
    // Build route: empty routePath means default dashboard route
    const route = routePath ? `/dashboard/${routePath}` : "/dashboard";

    return {
      type: "collapse",
      name: menuName,
      key: routeKey,
      icon: getIconComponent(menuItem.icon),
      route: route,
      component: null,
      noCollapse: true,
    };
  });
};
